// COL1 — the building collision tile buildings/col_<tx>_<ty>.bin, decoded by
// ONE module shared by the game server (require: game/buildcol.js, routes.js,
// the scripts) and the WebXR client (esbuild import: client/src/buildings.js),
// like ./spans.js. Dependency-free. The writer is pipeline/colbin.py (the
// buildings stage); encode() here is its byte-identical twin for the gate
// (scripts/check-glb.mjs section B).
//
// decode(bytes) returns EXACTLY the object the old col_<tx>_<ty>.json parsed
// to: {buildings: [{uuid, fp, y0, y1, roof, holes?, fp_src?}]} with plain JS
// arrays, key order as the writer emitted it, every value `cm / per_m`. With
// per_m = 100 a 2-dp JSON value and its decoded twin are the same double
// (both the correctly rounded quotient), so fp / y0 / y1 are bit-equal to the
// JSON; the roof tris were float32 noise on the cm grid (np.round of a
// float32 array) and decode to the cm value itself (|d| <= half a float32
// ulp, < 0.5 mm at 13 km). -0 decodes as 0.
//
// Layout (little-endian; every section starts 4-byte aligned, zero padding):
//   header, 32 bytes:
//     0  'COL1'            magic
//     4  u32 version       1
//     8  u32 per_m         quanta per metre (100: the 1 cm grid)
//    12  u32 E             entries
//    16  u32 R             rings (fp + holes, all entries)
//    20  u32 P             points (all rings)
//    24  u32 T             roof tris
//    28  u32 S             string-table bytes
//   sections, in order:
//     y0      i32[E]       cm
//     y1      i32[E]       cm
//     ringOff u32[E+1]     prefix into rings: entry e owns rings
//                          [ringOff[e], ringOff[e+1]) = nFp fp rings, then nHole holes
//     triOff  u32[E+1]     prefix into roof tris
//     strOff  u32[E+1]     prefix into the string table (the uuid's UTF-8 bytes)
//     nFp     u16[E]       fp ring count
//     nHole   u16[E]       hole ring count
//     fpSrc   u8[E]        0 none / 1 'roof' / 2 'passage'
//     flags   u8[E]        bit0 the entry has a uuid, bit1 it has a `holes`
//                          key (the writer emits one only when non-empty)
//     ptOff   u32[R+1]     prefix into points
//     pts     i32[2P]      cm, (x, z) per point
//     roof    i32[9T]      cm, (x, y, z) x 3 per tri
//     str     u8[S]        UTF-8 uuids
//   The file length must equal the end of the last section (padded).
// Deterministic: the same entries give the same bytes (no timestamps, no
// map iteration, padding zeroed).
'use strict';

const MAGIC = 0x314c4f43;   // 'COL1' read as a little-endian u32
const VERSION = 1;
const PER_M = 100;
const HEADER = 32;
const SRC = [null, 'roof', 'passage'];
const KEYS = new Set(['uuid', 'fp', 'y0', 'y1', 'roof', 'holes', 'fp_src']);
const F_UUID = 1, F_HOLES = 2;

const pad4 = (n) => (n + 3) & ~3;

// byte offsets of every section for the header counts
function sections(E, R, P, T, S) {
  const o = {};
  let p = HEADER;
  const put = (k, n) => { o[k] = p; p = pad4(p + n); };
  put('y0', 4 * E); put('y1', 4 * E);
  put('ringOff', 4 * (E + 1)); put('triOff', 4 * (E + 1)); put('strOff', 4 * (E + 1));
  put('nFp', 2 * E); put('nHole', 2 * E);
  put('fpSrc', E); put('flags', E);
  put('ptOff', 4 * (R + 1));
  put('pts', 8 * P); put('roof', 36 * T);
  put('str', S);
  o.end = p;
  return o;
}

let _td = null, _te = null;

function decode(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes
    : ArrayBuffer.isView(bytes) ? new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)
      : new Uint8Array(bytes);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (u8.byteLength < HEADER || dv.getUint32(0, true) !== MAGIC) throw new Error('COL1: bad magic');
  const ver = dv.getUint32(4, true);
  if (ver !== VERSION) throw new Error(`COL1: version ${ver}, this reader knows ${VERSION}`);
  const perM = dv.getUint32(8, true);
  if (!perM) throw new Error('COL1: per_m 0');
  const E = dv.getUint32(12, true), R = dv.getUint32(16, true), P = dv.getUint32(20, true);
  const T = dv.getUint32(24, true), S = dv.getUint32(28, true);
  const o = sections(E, R, P, T, S);
  if (o.end !== u8.byteLength) throw new Error(`COL1: ${u8.byteLength} bytes, the header says ${o.end}`);
  const u32 = (s, i) => dv.getUint32(o[s] + 4 * i, true);
  if (u32('ringOff', E) !== R || u32('triOff', E) !== T || u32('strOff', E) !== S || u32('ptOff', R) !== P) {
    throw new Error('COL1: prefix totals disagree with the header');
  }
  const ptAt = o.pts, roofAt = o.roof;
  const ring = (r) => {
    const a = u32('ptOff', r), b = u32('ptOff', r + 1);
    if (b < a) throw new Error('COL1: ring offsets not monotone');
    const out = new Array(b - a);
    for (let i = a, k = 0; i < b; i++, k++) {
      out[k] = [dv.getInt32(ptAt + 8 * i, true) / perM, dv.getInt32(ptAt + 8 * i + 4, true) / perM];
    }
    return out;
  };
  const buildings = new Array(E);
  for (let e = 0; e < E; e++) {
    const r0 = u32('ringOff', e), r1 = u32('ringOff', e + 1);
    const nFp = dv.getUint16(o.nFp + 2 * e, true), nHole = dv.getUint16(o.nHole + 2 * e, true);
    if (r1 - r0 !== nFp + nHole || r1 < r0) throw new Error(`COL1: entry ${e} ring counts disagree`);
    const flags = u8[o.flags + e], src = u8[o.fpSrc + e];
    if (flags & ~(F_UUID | F_HOLES) || src >= SRC.length) throw new Error(`COL1: entry ${e} bad flags / fp_src`);
    const b = {};
    if (flags & F_UUID) {
      const s0 = u32('strOff', e), s1 = u32('strOff', e + 1);
      if (s1 < s0) throw new Error('COL1: string offsets not monotone');
      if (!_td) _td = new TextDecoder('utf-8', { fatal: true });
      b.uuid = _td.decode(u8.subarray(o.str + s0, o.str + s1));
    }
    const fp = new Array(nFp);
    for (let k = 0; k < nFp; k++) fp[k] = ring(r0 + k);
    b.fp = fp;
    b.y0 = dv.getInt32(o.y0 + 4 * e, true) / perM;
    b.y1 = dv.getInt32(o.y1 + 4 * e, true) / perM;
    const t0 = u32('triOff', e), t1 = u32('triOff', e + 1);
    if (t1 < t0) throw new Error('COL1: tri offsets not monotone');
    const roof = new Array(t1 - t0);
    for (let t = t0, k = 0; t < t1; t++, k++) {
      const q = roofAt + 36 * t, tri = new Array(3);
      for (let v = 0; v < 3; v++) {
        tri[v] = [dv.getInt32(q + 12 * v, true) / perM, dv.getInt32(q + 12 * v + 4, true) / perM,
          dv.getInt32(q + 12 * v + 8, true) / perM];
      }
      roof[k] = tri;
    }
    b.roof = roof;
    if (flags & F_HOLES) {
      const holes = new Array(nHole);
      for (let k = 0; k < nHole; k++) holes[k] = ring(r0 + nFp + k);
      b.holes = holes;
    } else if (nHole) throw new Error(`COL1: entry ${e} has hole rings without the holes flag`);
    if (src) b.fp_src = SRC[src];
    buildings[e] = b;
  }
  return { buildings };
}

// --------------------------------------------------------------- encode
// The gate's twin of pipeline/colbin.py encode (same checks, same bytes).
// RAISES on an unknown key, a bad type, or a value off the cm grid: a 2-dp
// value (|v*100 - rint| <= 1e-3), or for roof tris also the float32 of a cm
// value (what np.round(float32, 2) left) while |v| < 65536 m (float32's half
// ulp < 0.5 cm there, so the cm is unambiguous); never drops data.
const f32 = Math.fround;
function cm(v, what, roof) {
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`COL1 encode: ${what} is not a finite number (${v})`);
  const c = Math.round(v * PER_M);
  const onGrid = Math.abs(v * PER_M - c) <= 1e-3 || (roof && Math.abs(v) < 65536 && f32(v) === v && f32(c / PER_M) === v);
  if (!onGrid) throw new Error(`COL1 encode: ${what} = ${v} is off the 1/${PER_M} m grid`);
  if (c > 0x7fffffff || c < -0x80000000) throw new Error(`COL1 encode: ${what} = ${v} overflows int32`);
  return c;
}

function encode(data) {
  const items = Array.isArray(data) ? data : data && data.buildings;
  if (!Array.isArray(items)) throw new Error('COL1 encode: expected {buildings: [...]}');
  if (!Array.isArray(data)) for (const k of Object.keys(data)) if (k !== 'buildings') throw new Error(`COL1 encode: unknown top-level key ${k}`);
  if (!_te) _te = new TextEncoder();
  const E = items.length;
  const y0 = [], y1 = [], nFp = [], nHole = [], fpSrc = [], flags = [], ringLen = [], pts = [], roof = [], strs = [];
  const triN = [], strN = [];
  const addRing = (r, what) => {
    if (!Array.isArray(r)) throw new Error(`COL1 encode: ${what} is not a ring`);
    for (const p of r) {
      if (!Array.isArray(p) || p.length !== 2) throw new Error(`COL1 encode: ${what} has a point that is not [x, z]`);
      pts.push(cm(p[0], what, false), cm(p[1], what, false));
    }
    ringLen.push(r.length);
  };
  items.forEach((b, e) => {
    for (const k of Object.keys(b)) if (!KEYS.has(k)) throw new Error(`COL1 encode: entry ${e} has unknown key ${k}`);
    let f = 0;
    if ('uuid' in b) {
      if (typeof b.uuid !== 'string') throw new Error(`COL1 encode: entry ${e} uuid is not a string`);
      f |= F_UUID;
      const s = _te.encode(b.uuid);
      strs.push(s); strN.push(s.length);
    } else strN.push(0);
    const fp = b.fp, holes = 'holes' in b ? b.holes : null;
    if (!Array.isArray(fp)) throw new Error(`COL1 encode: entry ${e} fp is not a list`);
    if (holes !== null && !Array.isArray(holes)) throw new Error(`COL1 encode: entry ${e} holes is not a list`);
    if (holes !== null) f |= F_HOLES;
    if (fp.length > 0xffff || (holes && holes.length > 0xffff)) throw new Error(`COL1 encode: entry ${e} has too many rings`);
    fp.forEach((r) => addRing(r, `entry ${e} fp`));
    (holes || []).forEach((r) => addRing(r, `entry ${e} holes`));
    nFp.push(fp.length); nHole.push(holes ? holes.length : 0);
    y0.push(cm(b.y0, `entry ${e} y0`, false)); y1.push(cm(b.y1, `entry ${e} y1`, false));
    if (!Array.isArray(b.roof)) throw new Error(`COL1 encode: entry ${e} roof is not a list`);
    for (const t of b.roof) {
      if (!Array.isArray(t) || t.length !== 3) throw new Error(`COL1 encode: entry ${e} roof tri is not 3 vertices`);
      for (const v of t) {
        if (!Array.isArray(v) || v.length !== 3) throw new Error(`COL1 encode: entry ${e} roof vertex is not [x, y, z]`);
        for (const c of v) roof.push(cm(c, `entry ${e} roof`, true));
      }
    }
    triN.push(b.roof.length);
    const src = 'fp_src' in b ? SRC.indexOf(b.fp_src) : 0;
    if (src <= 0 && 'fp_src' in b) throw new Error(`COL1 encode: entry ${e} unknown fp_src ${JSON.stringify(b.fp_src)}`);
    fpSrc.push(src); flags.push(f);
  });
  const R = ringLen.length, P = pts.length / 2, T = roof.length / 9;
  const S = strN.reduce((a, n) => a + n, 0);
  const o = sections(E, R, P, T, S);
  const u8 = new Uint8Array(o.end), dv = new DataView(u8.buffer);
  dv.setUint32(0, MAGIC, true); dv.setUint32(4, VERSION, true); dv.setUint32(8, PER_M, true);
  dv.setUint32(12, E, true); dv.setUint32(16, R, true); dv.setUint32(20, P, true);
  dv.setUint32(24, T, true); dv.setUint32(28, S, true);
  const prefix = (at, ns) => { let s = 0; dv.setUint32(at, 0, true); ns.forEach((n, i) => { s += n; dv.setUint32(at + 4 * (i + 1), s, true); }); };
  y0.forEach((v, i) => dv.setInt32(o.y0 + 4 * i, v, true));
  y1.forEach((v, i) => dv.setInt32(o.y1 + 4 * i, v, true));
  prefix(o.ringOff, nFp.map((n, i) => n + nHole[i]));
  prefix(o.triOff, triN);
  prefix(o.strOff, strN);
  nFp.forEach((v, i) => dv.setUint16(o.nFp + 2 * i, v, true));
  nHole.forEach((v, i) => dv.setUint16(o.nHole + 2 * i, v, true));
  u8.set(fpSrc, o.fpSrc); u8.set(flags, o.flags);
  prefix(o.ptOff, ringLen);
  pts.forEach((v, i) => dv.setInt32(o.pts + 4 * i, v, true));
  roof.forEach((v, i) => dv.setInt32(o.roof + 4 * i, v, true));
  let s = o.str;
  for (const b of strs) { u8.set(b, s); s += b.length; }
  return u8;
}

module.exports = { decode, encode, MAGIC, VERSION, PER_M };
